import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import { i18n } from "../../translate/i18n";

const haloBase = {
  position: "absolute",
  borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(148,163,184,0.12))",
  filter: "blur(8px)",
  animation: "$float 6s ease-in-out infinite",
  opacity: 0.75
};

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    background:
      "radial-gradient(circle at 20% 20%, rgba(30, 64, 175, 0.12), transparent 45%)," +
      "radial-gradient(circle at 80% 10%, rgba(236, 72, 153, 0.16), transparent 55%)," +
      "radial-gradient(circle at 50% 80%, rgba(14, 116, 144, 0.14), transparent 50%)," +
      "#0f172a",
    color: "#f8fafc"
  },
  halo: {
    ...haloBase,
    width: 320,
    height: 320
  },
  haloSecondary: {
    ...haloBase,
    width: 420,
    height: 420,
    animationDelay: "1.3s",
    animationDuration: "7s",
    opacity: 0.55
  },
  frame: {
    position: "relative",
    zIndex: 2,
    padding: "72px 96px",
    borderRadius: 32,
    background: "linear-gradient(160deg, rgba(15, 23, 42, 0.76), rgba(15, 23, 42, 0.52))",
    backdropFilter: "blur(12px)",
    boxShadow: "0 32px 80px rgba(15, 23, 42, 0.4)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 32,
    textAlign: "center",
    maxWidth: 480,
    margin: "0 24px"
  },
  glyphWrapper: {
    position: "relative",
    width: 112,
    height: 112
  },
  ring: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    background:
      "conic-gradient(from 90deg, rgba(148, 163, 184, 0.35), rgba(226, 232, 240, 0.25), rgba(148, 163, 184, 0.35))",
    mask: "radial-gradient(circle, transparent 58%, black 60%)",
    animation: "$spin 8s linear infinite"
  },
  orb: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: 36,
    height: 36,
    transform: "translate(-50%, -50%)",
    borderRadius: "50%",
    background: "linear-gradient(145deg, #e2e8f0, #94a3b8)",
    boxShadow:
      "0 12px 30px rgba(148, 163, 184, 0.45), inset -6px -6px 12px rgba(30, 41, 59, 0.6)",
    animation: "$pulse 2.6s ease-in-out infinite"
  },
  shimmer: {
    position: "absolute",
    inset: "12% 10%",
    borderRadius: "32px",
    border: "1px solid rgba(148, 163, 184, 0.35)",
    background:
      "linear-gradient(110deg, rgba(148, 163, 184, 0.12), rgba(248, 250, 252, 0.08), rgba(148, 163, 184, 0.12))",
    mask:
      "linear-gradient(105deg, transparent 5%, rgba(248, 250, 252, 0.8) 40%, transparent 65%)",
    animation: "$shimmer 5s ease-in-out infinite"
  },
  headline: {
    fontSize: "1.8rem",
    fontWeight: 600,
    letterSpacing: "0.04em"
  },
  subtitle: {
    fontSize: "1rem",
    color: "rgba(241, 245, 249, 0.78)",
    lineHeight: 1.6,
    maxWidth: 320
  },
  retryButton: {
    marginTop: 12,
    color: "#0f172a",
    fontWeight: 600,
    background: "#f8fafc",
    "&:hover": {
      background: "#e2e8f0"
    }
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translate(-60px, -30px)"
    },
    "50%": {
      transform: "translate(40px, 20px)"
    }
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)"
    },
    "100%": {
      transform: "rotate(360deg)"
    }
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "translate(-50%, -50%) scale(0.92)",
      boxShadow:
        "0 12px 28px rgba(148, 163, 184, 0.35), inset -6px -6px 12px rgba(30, 41, 59, 0.6)"
    },
    "50%": {
      transform: "translate(-50%, -50%) scale(1.08)",
      boxShadow:
        "0 14px 34px rgba(148, 163, 184, 0.5), inset -6px -6px 12px rgba(30, 41, 59, 0.4)"
    }
  },
  "@keyframes shimmer": {
    "0%": {
      transform: "translateX(-60%)"
    },
    "50%": {
      transform: "translateX(60%)"
    },
    "100%": {
      transform: "translateX(-60%)"
    }
  }
}));

const BrandingSplash = ({ variant = "loading", onRetry }) => {
  const classes = useStyles();
  const isError = variant === "error";
  const title = i18n.t(isError ? "brandingSplash.errorTitle" : "brandingSplash.loadingTitle");
  const subtitle = i18n.t(
    isError ? "brandingSplash.errorSubtitle" : "brandingSplash.loadingSubtitle"
  );

  return (
    <div className={classes.root}>
      <div className={classes.halo} />
      <div className={classes.haloSecondary} />

      <div className={classes.frame}>
        <div className={classes.glyphWrapper}>
          <div className={classes.ring} />
          <div className={classes.orb} />
          <div className={classes.shimmer} />
        </div>

        <div>
          <div className={classes.headline}>{title}</div>
          <div className={classes.subtitle}>{subtitle}</div>
        </div>

        {isError && onRetry ? (
          <Button className={classes.retryButton} variant="contained" onClick={onRetry}>
            {i18n.t("brandingSplash.retry")}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default BrandingSplash;
